import { useQuery, type QueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useAuth } from "@/hooks/use-auth";
import { FEEDBACK_API, type FeedbackBoardStatus } from "@shared/feedbackBoard";

// Query keys follow the default query fn: path segments are joined with "/", an object becomes the query string.
export const FEEDBACK_STATUS_KEY = [`${FEEDBACK_API}/status`] as const;
/** The session firm's list. Also the prefix of every item detail key. */
export const FEEDBACK_ITEMS_KEY = [`${FEEDBACK_API}/items`] as const;
export const FEEDBACK_ROADMAP_KEY = [`${FEEDBACK_API}/roadmap`] as const;
export const FEEDBACK_ADMIN_ITEMS_PATH = `${FEEDBACK_API}/admin/items`;
export const FEEDBACK_ADMIN_ITEMS_KEY = [FEEDBACK_ADMIN_ITEMS_PATH] as const;
/** Same key the sidebar uses for the firm switcher. */
export const USER_ORGANIZATIONS_KEY = ["/api/auth/user-organizations"] as const;

export function feedbackItemKey(id: string) {
  return [`${FEEDBACK_API}/items`, id] as const;
}

/** The full status query, for callers that need its loading state. */
export function useFeedbackBoardStatusQuery() {
  const { isAuthenticated } = useAuth();
  const query = useQuery<FeedbackBoardStatus>({
    queryKey: FEEDBACK_STATUS_KEY,
    enabled: isAuthenticated,
    // A 404 means the flag is off; retrying would only delay hiding the board.
    retry: false,
  });
  const status = query.data?.enabled === true ? query.data : undefined;
  return { status, isLoading: query.isLoading };
}

/**
 * The board status, or undefined when the board must stay hidden: signed out,
 * flag off (404), or any other error.
 */
export function useFeedbackBoardStatus(): FeedbackBoardStatus | undefined {
  return useFeedbackBoardStatusQuery().status;
}

/** After a status, publication or vote change: every list, every item and the roadmap. */
export function invalidateFeedbackQueries(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: FEEDBACK_ITEMS_KEY }),
    queryClient.invalidateQueries({ queryKey: FEEDBACK_ADMIN_ITEMS_KEY }),
    queryClient.invalidateQueries({ queryKey: FEEDBACK_ROADMAP_KEY }),
  ]);
}

export function formatNetVotes(netVotes: number): string {
  return netVotes > 0 ? `+${netVotes}` : String(netVotes);
}

export function formatFeedbackDate(iso: string, withTime = false): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, withTime ? "MMM d, yyyy h:mm a" : "MMM d, yyyy");
}
